import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import ProjectHeader from "../../../components/ProjectHeader";
import {
	TICKET_STATUS,
	TICKET_TYPES,
	TICKET_TYPE_LABELS,
} from "../../../constants/ticket-types";
import useProjectPermission from "../../../hooks/useProjectPermission";
import {
	useApproveTicketMutation,
	useCreateTicketMutation,
	useDeleteTicketMutation,
	useGetProjectTicketsQuery,
	useUpdateTicketMutation,
} from "../../../store/services/projectTicket";
import { useGetProjectTicketCategoriesQuery } from "../../../store/services/ticketCategoriesService";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import {
	formatCurrencyToLocale,
	getFormattedDate,
	showToast,
} from "../../../utils/method";
import {
	canApproveTicketRow,
	canEditTicketRow,
	filterTicketsByViewLevel,
	formatTicketUserName,
	getTicketAssigneeUser,
	getTicketCreatorUser,
} from "../../../utils/projectPermissionHelpers";
import "./project-tickets.scss";
import TicketFormModal from "./ticket-form-modal";
import {
	PROJECT_AREA,
	PROJECT_ACTION,
	TICKET_ACCESS_LEVEL,
} from "../../../constant/projectPermissions";

const getTicketColumns = (
	t,
	{
		onEdit,
		onDelete,
		onApprove,
		canEditRow,
		canDeleteRow,
		canApproveRow,
		requireTicketApproval,
	},
) => [
	{
		field: "createdAt",
		headerName: t("common.created"),
		headerTooltip: t("common.created"),
		unSortIcon: true,
		maxWidth: 130,
		valueFormatter: (params) => getFormattedDate(params.value),
	},
	{
		field: "displayCode",
		headerName: t("tickets.display-code"),
		headerTooltip: t("tickets.display-code"),
		unSortIcon: true,
		maxWidth: 120,
	},
	{
		field: "creator",
		headerName: t("projects.tickets.creator"),
		headerTooltip: t("projects.tickets.creator"),
		filter: true,
		sortable: true,
		unSortIcon: true,
		maxWidth: 160,
		valueGetter: (params) => {
			const creator = getTicketCreatorUser(params.data);
			if (!creator) return t("hierarchical-positions.unknown");
			return (
				formatTicketUserName(creator, {
					unknownLabel: t("hierarchical-positions.unknown"),
				}) || t("hierarchical-positions.unknown")
			);
		},
	},
	{
		field: "assignee",
		headerName: t("projects.tickets.assignee"),
		headerTooltip: t("projects.tickets.assignee"),
		filter: true,
		sortable: true,
		unSortIcon: true,
		maxWidth: 160,
		valueGetter: (params) =>
			formatTicketUserName(getTicketAssigneeUser(params.data), {
				unassignedLabel: t("projects.tickets.unassigned"),
			}) || t("projects.tickets.unassigned"),
	},
	{
		field: "ticketType",
		headerName: t("tickets.ticket-type"),
		headerTooltip: t("tickets.ticket-type"),
		unSortIcon: true,
		maxWidth: 200,
		valueFormatter: (params) => {
			const typeKey = params.value;
			return (
				t(`tickets.type.${typeKey}`) ||
				TICKET_TYPE_LABELS[typeKey] ||
				typeKey
			);
		},
	},
	{
		field: "projectTicketCategory.name",
		headerName: t("projects.ticket-categories"),
		headerTooltip: t("projects.ticket-categories"),
		unSortIcon: true,
		maxWidth: 180,
	},
	{
		field: "summary",
		headerName: t("tickets.summary"),
		headerTooltip: t("tickets.summary"),
		unSortIcon: true,
		flex: 1,
		minWidth: 200,
	},
	{
		field: "amount",
		headerName: t("tickets.amount"),
		headerTooltip: t("tickets.amount"),
		unSortIcon: true,
		maxWidth: 150,
		type: "numericColumn",
		valueFormatter: (params) =>
			params.value ? formatCurrencyToLocale(params.value) : "€0.00",
	},
	{
		field: "status",
		headerName: t("common.status"),
		headerTooltip: t("common.status"),
		unSortIcon: true,
		maxWidth: 140,
		cellRenderer: (params) => {
			const status = params.value;
			const statusColors = {
				OPEN: "primary",
				IN_PROGRESS: "warning",
				SIGNED_OFF: "info",
				CLOSED: "success",
			};
			const color = statusColors[status] || "secondary";
			return (
				<span className={`badge bg-${color}`}>
					{t(`tickets.status.${status}`)}
				</span>
			);
		},
	},
	{
		// Combined approval cell: approval state + approver + approval date, with
		// the ticket deadline folded in (replaces the standalone Deadline column).
		colId: "approval",
		field: "approvedForInvoiceAt",
		headerName: t("tickets.approval"),
		headerTooltip: t("tickets.approval"),
		unSortIcon: true,
		minWidth: 190,
		maxWidth: 240,
		wrapText: true,
		autoHeight: true,
		cellRenderer: (params) => {
			const data = params.data || {};
			const approved = !!data.approvedForInvoiceAt;
			const approverName = formatTicketUserName(data.approvedByUser, {
				unknownLabel: t("hierarchical-positions.unknown"),
			});
			const deadline = data.deadline
				? getFormattedDate(data.deadline)
				: null;
			return (
				<div className="ticket-approval-cell">
					<div className="ticket-approval-cell__state">
						<span
							className={`badge ${approved ? "bg-success" : "bg-secondary"}`}
						>
							{approved
								? t("tickets.approved")
								: t("tickets.not-approved")}
						</span>
						{approved && approverName && (
							<span className="ticket-approval-cell__by">
								{approverName}
							</span>
						)}
					</div>
					{approved && (
						<span className="ticket-approval-cell__meta">
							{t("tickets.approved-on", {
								date: getFormattedDate(
									data.approvedForInvoiceAt,
								),
							})}
						</span>
					)}
					{deadline && (
						<span className="ticket-approval-cell__meta">
							{t("tickets.due", { date: deadline })}
						</span>
					)}
				</div>
			);
		},
	},
	createActionColumn({
		t,
		actions: [
			{
				// Approved tickets are read-only - surface a "View" affordance
				// instead of "Edit" (the modal opens in read-only mode either way).
				label: (data) => (data.approvedForInvoiceAt ? "view" : "edit"),
				onClick: onEdit,
				show: (data) =>
					canEditRow(data) || !!data.approvedForInvoiceAt,
			},
			{
				// Approval authority is assignee-or-admin (hard-enforced on the
				// backend), not the tickets.approve permission - so no
				// projectPermission gate here.
				label: "tickets.approve",
				onClick: onApprove,
				show: (data) =>
					requireTicketApproval &&
					!data.approvedForInvoiceAt &&
					canApproveRow(data),
			},
			{
				label: "delete",
				onClick: onDelete,
				danger: true,
				show: (data) => canDeleteRow(data),
			},
		],
	}),
];

function ProjectTicketsPage() {
	const { t } = useTranslation();

	const projectDetails = useSelector(selectProjectDetails);
	const currentUser = useSelector((state) => state.auth?.user);
	const currentUserId = currentUser?.id;

	const {
		can,
		isProjectAdmin,
		isLoading: permissionLoading,
		ticketViewLevel,
		ticketEditLevel,
		filterByCategoryScope,
		requireTicketApproval,
	} = useProjectPermission();

	const canViewTickets =
		!permissionLoading && ticketViewLevel !== TICKET_ACCESS_LEVEL.NONE;
	const canCreateTicket = can(PROJECT_AREA.TICKETS, PROJECT_ACTION.CREATE);
	const canDeleteTicket = can(PROJECT_AREA.TICKETS, PROJECT_ACTION.DELETE);
	const canApproveTicket = can(PROJECT_AREA.TICKETS, PROJECT_ACTION.APPROVE);

	const [showTicketModal, setShowTicketModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showApproveModal, setShowApproveModal] = useState(false);
	const [ticketToApprove, setTicketToApprove] = useState(null);
	const [selectedTicket, setSelectedTicket] = useState(null);
	const [saving, setSaving] = useState(false);
	const [approving, setApproving] = useState(false);

	const [filterItems, setFilterItems] = useState(
		getSearchAddFilter({
			addTextKey: "tickets.add-ticket",
		}),
	);

	const { id: projectId } = projectDetails || {};

	const [page, setPage] = useState(1);
	const [queryFilters, setQueryFilters] = useState({
		search: "",
		ticketType: "",
		status: "",
	});

	const {
		data: ticketsResponse,
		isFetching: loading,
	} = useGetProjectTicketsQuery(
		{ projectId, page, limit: 50, ...queryFilters },
		{ skip: !projectId || !canViewTickets },
	);

	const tickets = useMemo(
		() => ticketsResponse?.items ?? [],
		[ticketsResponse],
	);
	const ticketsHasMore = page < (ticketsResponse?.after?.totalPages ?? 0);

	const [createTicket] = useCreateTicketMutation();
	const [updateTicket] = useUpdateTicketMutation();
	const [deleteTicket] = useDeleteTicketMutation();
	const [approveTicket] = useApproveTicketMutation();

	const visibleTickets = useMemo(() => {
		const scoped = filterByCategoryScope(
			tickets || [],
			(ticket) => ticket.projectTicketCategoryId,
		);
		return filterTicketsByViewLevel(
			scoped,
			ticketViewLevel,
			currentUserId,
		);
	}, [
		tickets,
		filterByCategoryScope,
		ticketViewLevel,
		currentUserId,
	]);

	const canShowAddButton = canCreateTicket;

	const canEditRow = useCallback(
		(ticket) =>
			canEditTicketRow(
				ticket,
				ticketEditLevel,
				currentUserId,
				can(PROJECT_AREA.TICKETS, PROJECT_ACTION.EDIT),
			),
		[ticketEditLevel, currentUserId, can],
	);

	const canDeleteRow = useCallback(
		(ticket) =>
			canEditTicketRow(
				ticket,
				ticketEditLevel,
				currentUserId,
				canDeleteTicket,
			),
		[ticketEditLevel, currentUserId, canDeleteTicket],
	);

	// Separation of duties: admins always; otherwise the approve permission is
	// required and the approver must not be the ticket's assignee or creator.
	const canApproveRow = useCallback(
		(ticket) =>
			canApproveTicketRow(ticket, {
				isProjectAdmin,
				canApprove: canApproveTicket,
				userId: currentUserId,
			}),
		[isProjectAdmin, canApproveTicket, currentUserId],
	);

	// Pre-fetch ticket categories so any child filters/dropdowns have data.
	useGetProjectTicketCategoriesQuery(
		{ projectId },
		{ skip: !projectId || !canViewTickets },
	);

	useEffect(() => {
		const typeFilterItem = {
			key: "ticketType",
			type: "dropdown",
			labelKey: "tickets.filter-by-type",
			value: "",
			options: [
				{ value: "", label: t("tickets.all-types") },
				...Object.values(TICKET_TYPES).map((type) => ({
					value: type,
					label: t(`tickets.type.${type}`),
				})),
			],
		};

		const statusFilterItem = {
			key: "status",
			type: "dropdown",
			labelKey: "tickets.filter-by-status",
			value: "",
			options: [
				{ value: "", label: t("tickets.all-statuses") },
				...Object.values(TICKET_STATUS).map((status) => ({
					value: status,
					label: t(`tickets.status.${status}`),
				})),
			],
		};

		setFilterItems(
			getSearchAddFilter(
				canShowAddButton
					? { addTextKey: "tickets.add-ticket" }
					: { hideAddButton: true },
				[typeFilterItem, statusFilterItem],
			),
		);
	}, [t, canShowAddButton]);

	const applyFiltersAndResetPage = useCallback(
		(overrides = {}) => {
			setQueryFilters((prev) => ({ ...prev, ...overrides }));
			setPage(1);
		},
		[],
	);

	const onGridBodyScrollEnd = useCallback(
		(event) => {
			if (!ticketsHasMore || loading) return;
			const { api } = event;
			const lastDisplayed = api.getLastDisplayedRowIndex();
			const totalDisplayed = api.getDisplayedRowCount();
			if (lastDisplayed >= totalDisplayed - 3) {
				setPage((p) => p + 1);
			}
		},
		[ticketsHasMore, loading],
	);

	const handleAddTicket = () => {
		setSelectedTicket(null);
		setShowTicketModal(true);
	};

	const handleEditTicket = useCallback((ticket) => {
		setSelectedTicket(ticket);
		setShowTicketModal(true);
	}, []);

	const handleDeleteClick = useCallback((ticket) => {
		setSelectedTicket(ticket);
		setShowDeleteModal(true);
	}, []);

	// Open the confirmation popup instead of approving immediately, to avoid
	// accidental approval (approval is irreversible and locks the ticket).
	const handleApproveClick = useCallback(
		(ticket) => {
			if (!ticket || ticket.approvedForInvoiceAt) return;
			if (!canApproveRow(ticket)) return;
			setTicketToApprove(ticket);
			setShowApproveModal(true);
		},
		[canApproveRow],
	);

	const handleConfirmApprove = useCallback(async () => {
		if (
			!ticketToApprove ||
			!requireTicketApproval ||
			ticketToApprove.approvedForInvoiceAt
		)
			return;
		try {
			setApproving(true);
			await approveTicket({
				projectId,
				ticketId: ticketToApprove.id,
			}).unwrap();
			showToast(t("projects.tickets.approved"), { type: "success" });
			setShowApproveModal(false);
			setTicketToApprove(null);
		} catch (error) {
			showToast(
				error?.data?.message || error?.message || t("errors.general"),
				{ type: "error" },
			);
		} finally {
			setApproving(false);
		}
	}, [projectId, requireTicketApproval, approveTicket, ticketToApprove, t]);

	const handleSubmitTicket = async (payload) => {
		try {
			setSaving(true);

			if (selectedTicket) {
				await updateTicket({
					projectId,
					ticketId: selectedTicket.id,
					...payload,
				}).unwrap();
				showToast(t("tickets.update-success"), { type: "success" });
			} else {
				await createTicket({
					projectId,
					categoryId: payload.projectTicketCategoryId,
					...payload,
				}).unwrap();
				showToast(t("tickets.create-success"), { type: "success" });
			}

			setShowTicketModal(false);
			setSelectedTicket(null);
			// RTK invalidatesTags will auto-refetch the LIST.
		} catch (error) {
			const message =
				typeof error === "string"
					? error
					: error?.data?.message || error?.message || t("errors.general");
			showToast(message, { type: "error" });
		} finally {
			setSaving(false);
		}
	};

	const handleConfirmDelete = async () => {
		if (!selectedTicket) return;

		try {
			await deleteTicket({ projectId, ticketId: selectedTicket.id }).unwrap();

			showToast(t("tickets.delete-success"), { type: "success" });
			setShowDeleteModal(false);
			setSelectedTicket(null);
		} catch (error) {
			showToast(error?.data?.message || error?.message || t("errors.general"), { type: "error" });
		}
	};

	const onFilterAction = (index, key, info) => {
		if (key === "add") {
			handleAddTicket();
			return;
		}

		const localFilterItems = [...filterItems];
		localFilterItems[index].value = info?.value || "";
		setFilterItems(localFilterItems);

		const newVal = info?.value || "";
		applyFiltersAndResetPage({ [key]: newVal });
	};

	const viewScopeMessageKey =
		ticketViewLevel === TICKET_ACCESS_LEVEL.OWN
			? "projects.tickets.view-scope-own"
			: ticketViewLevel === TICKET_ACCESS_LEVEL.ASSIGNED_AND_OWN
				? "projects.tickets.view-scope-assigned"
				: null;

	const columns = useMemo(
		() =>
			getTicketColumns(t, {
				onEdit: handleEditTicket,
				onDelete: handleDeleteClick,
				onApprove: handleApproveClick,
				canEditRow,
				canDeleteRow,
				canApproveRow,
				requireTicketApproval,
			}),
		[
			t,
			handleEditTicket,
			handleDeleteClick,
			handleApproveClick,
			canEditRow,
			canDeleteRow,
			canApproveRow,
			requireTicketApproval,
		],
	);

	if (permissionLoading) {
		return (
			<>
				<ProjectHeader
					projectName={projectDetails?.name}
					tabTitle={t("tickets.title")}
				/>
				<div className="grid-container p-4 text-center">
					<span>{t("common.loading")}</span>
				</div>
			</>
		);
	}

	if (!canViewTickets) {
		return (
			<>
				<ProjectHeader
					projectName={projectDetails?.name}
					tabTitle={t("tickets.title")}
				/>
				<NoPermissionAlert messageKey="projects.tickets.view-level-none" />
			</>
		);
	}

	return (
		<>
			<ProjectHeader
				projectName={projectDetails?.name}
				tabTitle={t("tickets.title")}
			/>
			<div className="grid-container project-tickets-page">
				<FilterBar
					items={filterItems}
					onFilterAction={onFilterAction}
					canAdd={canShowAddButton}
				/>

				{viewScopeMessageKey && (
					<div
						className="alert alert-light border mb-3 py-2 px-3 small text-muted project-tickets__view-scope"
						role="status"
					>
						{t(viewScopeMessageKey)}
					</div>
				)}

				<AgGridAtom
					rowData={visibleTickets}
					columnDefs={columns}
					context={{ projectCan: can }}
					defaultColDef={{
						sortable: true,
						filter: true,
					}}
					overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("tickets.no-tickets")}</span>`}
					getRowId={(params) => params.data.id}
					onBodyScrollEnd={onGridBodyScrollEnd}
				/>
			</div>

			<TicketFormModal
				show={showTicketModal}
				onHide={() => {
					setShowTicketModal(false);
					setSelectedTicket(null);
				}}
				onSubmit={handleSubmitTicket}
				onApprove={(ticketRow) => {
					// Close the form modal and route through the confirmation popup.
					setShowTicketModal(false);
					setSelectedTicket(null);
					handleApproveClick(ticketRow);
				}}
				ticket={selectedTicket}
				projectId={projectId}
				loading={saving}
				canEditAssignee={can(PROJECT_AREA.TICKETS, PROJECT_ACTION.EDIT)}
				canEditTicket={
					selectedTicket ? canEditRow(selectedTicket) : true
				}
				canApprove={canApproveRow(selectedTicket)}
				requireTicketApproval={requireTicketApproval}
			/>

			<ModalAtom
				show={showDeleteModal}
				handleClose={() => {
					setShowDeleteModal(false);
					setSelectedTicket(null);
				}}
				title={t("tickets.delete-ticket")}
				handleSubmit={handleConfirmDelete}
				saveText={saving ? "common.deleting" : "delete"}
				disabled={saving}
				saveButtonVariant="danger"
				body={
					<>
						<p>{t("tickets.delete-ticket-warning")}</p>
						{selectedTicket && (
							<div className="alert alert-info">
								<strong>{selectedTicket.displayCode}</strong> -{" "}
								{selectedTicket.summary}
							</div>
						)}
					</>
				}
			/>

			<ModalAtom
				show={showApproveModal}
				handleClose={() => {
					setShowApproveModal(false);
					setTicketToApprove(null);
				}}
				title={t("projects.tickets.approve-confirm-title")}
				handleSubmit={handleConfirmApprove}
				saveText={approving ? "common.loading" : "tickets.approve"}
				disabled={approving}
				saveButtonVariant="success"
				body={
					<>
						<p>{t("projects.tickets.approve-confirm-warning")}</p>
						{ticketToApprove && (
							<div className="alert alert-info">
								<strong>{ticketToApprove.displayCode}</strong> -{" "}
								{ticketToApprove.summary}
							</div>
						)}
					</>
				}
			/>
		</>
	);
}

export default ProjectTicketsPage;
